import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthCallback = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState('processing');
    const [error, setError] = useState('');

    // Helper function to fetch with retry logic
    const fetchWithRetry = async (url, options = {}, maxRetries = 3, retryDelay = 1000) => {
        let lastError;
        for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
                const response = await fetch(url, options);
                if (response.ok) {
                    return response;
                }
                
                // If it's a server error (5xx) or network error, retry
                if (response.status >= 500 || response.status === 0) {
                    lastError = new Error(`Server error: ${response.status}`);
                    if (attempt < maxRetries - 1) {
                        // Exponential backoff
                        const delay = retryDelay * Math.pow(2, attempt);
                        console.log(`[AuthCallback] Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`);
                        await new Promise(resolve => setTimeout(resolve, delay));
                        continue;
                    }
                } else {
                    // Non-retryable error (4xx except 500)
                    throw new Error(`Request failed: ${response.status}`);
                }
            } catch (err) {
                lastError = err;
                if (attempt < maxRetries - 1) {
                    // Exponential backoff for network errors
                    const delay = retryDelay * Math.pow(2, attempt);
                    console.log(`[AuthCallback] Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`);
                    await new Promise(resolve => setTimeout(resolve, delay));
                }
            }
        }
        throw lastError || new Error('Request failed after retries');
    };

    useEffect(() => {
        let isMounted = true; // Prevent state updates if unmounted
        
        const handleCallback = async () => {
            try {
                if (!isMounted) return;
                setStatus('processing');
                
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                
                // Extract session token from URL
                const params = new URLSearchParams(window.location.search);
                const sessionToken = params.get('session_token');
                
                if (!sessionToken) {
                    throw new Error('No session token received');
                }
                
                // Check if we've already processed this token (prevent double-run in React Strict Mode)
                const processedToken = sessionStorage.getItem('processed_session_token');
                if (processedToken === sessionToken) {
                    console.log('Token already processed, verifying authentication...');
                    // Small delay to ensure cookies are set from the first run
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                    // Token already processed, just verify authentication with retry
                    const userResponse = await fetchWithRetry(`${apiUrl}/v1/auth/me`, {
                        credentials: 'include'
                    });
                    
                    const userData = await userResponse.json();
                    
                    if (!isMounted) return;
                    
                            // Already have user data, proceed to routing
                            const existingUserData = localStorage.getItem('currentUser');
                            if (!existingUserData) {
                                localStorage.setItem('currentUser', JSON.stringify(userData));
                                localStorage.setItem('hasLoggedIn', 'true');
                                // Small delay to ensure localStorage is persisted
                                await new Promise(resolve => setTimeout(resolve, 100));
                            }
                            
                            // Route based on families
                    const familiesResponse = await fetch(`${apiUrl}/v1/families`, {
                        credentials: 'include'
                    });
                    
                    if (!familiesResponse.ok) {
                        throw new Error('Failed to fetch families');
                    }
                    
                    const families = await familiesResponse.json();
                    
                    if (!isMounted) return;
                    
                    if (families.length === 0) {
                        navigate('/famlis', { replace: true });
                    } else {
                        // Auto-select most recently created family
                        const sortedFamilies = [...families].sort((a, b) => {
                            return new Date(b.created_at) - new Date(a.created_at);
                        });
                        localStorage.setItem('selectedFamilyId', sortedFamilies[0].id);
                        navigate('/dashboard', { replace: true });
                    }
                    return;
                }
                
                // Mark token as processed BEFORE making the request (prevent race condition)
                sessionStorage.setItem('processed_session_token', sessionToken);
                
                // Exchange session token for HTTP-only cookies (first time only)
                const exchangeResponse = await fetch(`${apiUrl}/v1/auth/exchange-session`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({ session_token: sessionToken })
                });
                
                if (!exchangeResponse.ok) {
                    const errorData = await exchangeResponse.json().catch(() => ({}));
                    console.error('Session exchange failed:', errorData);
                    
                    // If token was already used (race condition in React Strict Mode), 
                    // just verify we have valid cookies
                    if (exchangeResponse.status === 401) {
                        console.log('Token already used, verifying cookies...');
                        try {
                            const verifyResponse = await fetchWithRetry(`${apiUrl}/v1/auth/me`, {
                                credentials: 'include'
                            });
                            
                            console.log('Cookies are valid, continuing...');
                            const userData = await verifyResponse.json();
                            
                            if (!isMounted) return;
                            
                            localStorage.setItem('currentUser', JSON.stringify(userData));
                            localStorage.setItem('hasLoggedIn', 'true');
                            
                            // Small delay to ensure localStorage is persisted
                            await new Promise(resolve => setTimeout(resolve, 100));
                            
                            // Route based on families
                            const familiesResponse = await fetch(`${apiUrl}/v1/families`, {
                                credentials: 'include'
                            });
                            
                            if (!familiesResponse.ok) {
                                throw new Error('Failed to fetch families');
                            }
                            
                            const families = await familiesResponse.json();
                            
                            if (!isMounted) return;
                            
                            if (families.length === 0) {
                                navigate('/famlis', { replace: true });
                            } else {
                                // Auto-select most recently created family
                                const sortedFamilies = [...families].sort((a, b) => {
                                    return new Date(b.created_at) - new Date(a.created_at);
                                });
                                localStorage.setItem('selectedFamilyId', sortedFamilies[0].id);
                                navigate('/dashboard', { replace: true });
                            }
                            return;
                        } catch (verifyErr) {
                            console.error('Cookie verification failed:', verifyErr);
                            throw new Error('Failed to verify authentication');
                        }
                    }
                    
                    throw new Error('Failed to establish session');
                }
                
                if (!isMounted) return;
                
                // Now fetch current user (cookies should be set) with retry
                const userResponse = await fetchWithRetry(`${apiUrl}/v1/auth/me`, {
                    credentials: 'include' // Important: include cookies
                });
                
                const userData = await userResponse.json();
                
                // Store user data in localStorage for quick access
                console.log('[AuthCallback] Setting localStorage:', { userData });
                localStorage.setItem('currentUser', JSON.stringify(userData));
                localStorage.setItem('hasLoggedIn', 'true');
                console.log('[AuthCallback] localStorage set:', {
                    currentUser: localStorage.getItem('currentUser'),
                    hasLoggedIn: localStorage.getItem('hasLoggedIn')
                });
                
                // Small delay to ensure localStorage is persisted
                await new Promise(resolve => setTimeout(resolve, 100));
                
                // Fetch user's families from backend API
                const familiesResponse = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include' // Important: include cookies
                });
                
                if (!familiesResponse.ok) {
                    throw new Error('Failed to fetch families');
                }
                
                const families = await familiesResponse.json();
                
                console.log('[AuthCallback] Families fetched:', { familyCount: families.length, families });
                
                if (!isMounted) return;
                
                if (families.length === 0) {
                    // No families - route to famlis page to create one
                    console.log('[AuthCallback] No families - navigating to /famlis');
                    console.log('[AuthCallback] Final localStorage check before nav:', {
                        currentUser: localStorage.getItem('currentUser'),
                        hasLoggedIn: localStorage.getItem('hasLoggedIn')
                    });
                    navigate('/famlis', { replace: true });
                } else {
                    // Has families - auto-select most recently created/joined family and go to dashboard
                    // Sort families by created_at date (most recent first)
                    const sortedFamilies = [...families].sort((a, b) => {
                        const dateA = new Date(a.created_at);
                        const dateB = new Date(b.created_at);
                        return dateB - dateA; // Most recent first
                    });
                    
                    const selectedFamily = sortedFamilies[0];
                    
                    console.log('[AuthCallback] Auto-selecting most recent family and navigating to /dashboard');
                    console.log('[AuthCallback] Selected family:', selectedFamily.name, 'ID:', selectedFamily.id);
                    
                    localStorage.setItem('selectedFamilyId', selectedFamily.id);
                    navigate('/dashboard', { replace: true });
                }
            } catch (err) {
                console.error('Auth callback error:', err);
                setStatus('error');
                setError(err.message || 'Authentication failed. Please try again.');
                
                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login', { replace: true });
                }, 3000);
            }
        };
        
        handleCallback();
        
        // Cleanup function
        return () => {
            isMounted = false;
        };
    }, [navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
                {status === 'processing' && (
                    <div>
                        <div className="mb-4">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">Signing you in...</h2>
                        <p className="text-gray-600">Please wait while we complete your authentication.</p>
                    </div>
                )}
                
                {status === 'error' && (
                    <div>
                        <div className="mb-4">
                            <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">Authentication Failed</h2>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <p className="text-sm text-gray-500">Redirecting to login...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuthCallback;

